import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CategoryManager from '@/components/blog/category-manager';
import TiptapEditor from '@/components/blog/tiptap-editor';
import type { SidebarConfig } from '@/config/sidebarConfig';
import type { ArticleCategory, ArticleCategoryScope } from '@/lib/api/blogApi';
import { ArticleStatus } from '@shared/enums/domain.enum';
import { ArrowLeft, Save, Settings, Upload } from 'lucide-react';

import DashboardLayout from '../layout/DashboardLayout';

export type BlogFormState = {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  categoryId: number;
  status: ArticleStatus;
  featured: boolean;
  readTimeMinutes: string;
  tagsInput: string;
};

export type BlogFormErrors = Partial<Record<keyof BlogFormState | 'categoryId' | 'content', string>>;

interface BlogFormProps {
  mode: 'create' | 'edit';
  sidebarConfig: SidebarConfig;
  listRoute: string;
  form: BlogFormState;
  errors: BlogFormErrors;
  onFieldChange: <K extends keyof BlogFormState>(key: K, value: BlogFormState[K]) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  categories: ArticleCategory[] | undefined;
  showCategoryManager?: boolean;
  /**
   * Blog dont on gere la taxonomie (Lot A bis). Absent = referentiel
   * editorial BoulesConnect. Transmis tel quel au CategoryManager.
   */
  categoryScope?: ArticleCategoryScope;
  showFeatured?: boolean;
  coverPreview: string | null;
  coverFileName?: string;
  onCoverChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCoverRemove: () => void;
  /** Edit mode only : show "Changer l'image" link when existing cover + no new file */
  allowCoverReplace?: boolean;
}

export default function BlogForm({
  mode,
  sidebarConfig,
  listRoute,
  form,
  errors,
  onFieldChange,
  onSubmit,
  isPending,
  categories,
  showCategoryManager = false,
  categoryScope,
  showFeatured = false,
  coverPreview,
  coverFileName,
  onCoverChange,
  onCoverRemove,
  allowCoverReplace = false,
}: BlogFormProps) {
  const navigate = useNavigate();
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const isCreate = mode === 'create';
  const title = isCreate ? 'Nouvel article' : "Modifier l'article";
  const submitLabel = isCreate ? "Creer l'article" : 'Enregistrer';
  const pendingLabel = isCreate ? 'Creation...' : 'Mise a jour...';

  return (
    <DashboardLayout sidebarConfig={sidebarConfig}>
      <div className='p-8'>
        <button
          onClick={() => navigate(listRoute)}
          className='mb-4 inline-flex items-center gap-1.5 text-sm text-slate-600 transition hover:text-slate-900'
        >
          <ArrowLeft className='h-4 w-4' />
          Retour aux articles
        </button>

        <h1 className='mb-8 text-3xl font-bold text-slate-900'>{title}</h1>

        <form onSubmit={onSubmit} className='space-y-6 max-w-4xl'>
          <Field label='Titre' error={errors.title}>
            <input
              type='text'
              value={form.title}
              onChange={e => onFieldChange('title', e.target.value)}
              placeholder="Titre de l'article"
              className={inputClass(errors.title)}
            />
          </Field>

          <Field label={isCreate ? 'Slug (optionnel)' : 'Slug'} error={errors.slug}>
            <input
              type='text'
              value={form.slug}
              onChange={e => onFieldChange('slug', e.target.value)}
              placeholder={isCreate ? 'Auto-genere a partir du titre si vide' : undefined}
              className={inputClass(errors.slug)}
            />
          </Field>

          <Field label='Extrait' error={errors.excerpt}>
            <textarea
              value={form.excerpt}
              onChange={e => onFieldChange('excerpt', e.target.value)}
              placeholder={isCreate ? "Resume court de l'article..." : undefined}
              rows={3}
              className={inputClass(errors.excerpt)}
            />
          </Field>

          <Field label='Contenu' error={errors.content}>
            <TiptapEditor value={form.content} onChange={v => onFieldChange('content', v)} />
          </Field>

          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            <Field label='Categorie' error={errors.categoryId}>
              <div className='flex gap-2'>
                <select
                  value={form.categoryId}
                  onChange={e => onFieldChange('categoryId', Number(e.target.value))}
                  className={`flex-1 ${inputClass(errors.categoryId)}`}
                >
                  <option value={0}>-- Selectionnez --</option>
                  {(categories ?? []).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {showCategoryManager && (
                  <button
                    type='button'
                    onClick={() => setCatManagerOpen(true)}
                    className='rounded-lg border border-slate-200 px-3 py-2 text-slate-600 transition hover:bg-slate-50'
                    title='Gerer les categories'
                  >
                    <Settings className='h-4 w-4' />
                  </button>
                )}
              </div>
            </Field>

            <Field label='Statut'>
              <select
                value={form.status}
                onChange={e => onFieldChange('status', e.target.value as ArticleStatus)}
                className={inputClass()}
              >
                <option value={ArticleStatus.DRAFT}>Brouillon</option>
                <option value={ArticleStatus.PUBLISHED}>Publie</option>
              </select>
            </Field>
          </div>

          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            <Field label='Temps de lecture (min, optionnel)'>
              <input
                type='number'
                value={form.readTimeMinutes}
                onChange={e => onFieldChange('readTimeMinutes', e.target.value)}
                min={1}
                max={120}
                className={inputClass()}
              />
            </Field>

            {showFeatured && (
              <div className='flex items-center gap-3 pt-7'>
                <input
                  id='featured'
                  type='checkbox'
                  checked={form.featured}
                  onChange={e => onFieldChange('featured', e.target.checked)}
                  className='h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500'
                />
                <label htmlFor='featured' className='text-sm font-medium text-slate-900'>
                  Article mis en avant
                </label>
              </div>
            )}
          </div>

          <Field label='Image de couverture'>
            <div className='flex items-start gap-4'>
              {coverPreview ? (
                <div className='relative h-32 w-48 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200'>
                  <img src={coverPreview} alt='Apercu' className='h-full w-full object-cover' />
                  <button
                    type='button'
                    onClick={onCoverRemove}
                    className='absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white transition hover:bg-black/80'
                  >
                    <span className='sr-only'>Supprimer</span>
                    <svg className='h-3.5 w-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className='flex h-32 w-48 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 transition hover:border-blue-400 hover:text-blue-600'>
                  <Upload className='h-6 w-6' />
                  <span className='text-xs font-medium'>Choisir une image</span>
                  <input type='file' accept='image/*' onChange={onCoverChange} className='hidden' />
                </label>
              )}
              {coverFileName && <p className='pt-1 text-xs text-slate-500'>{coverFileName}</p>}
              {allowCoverReplace && !coverFileName && coverPreview && (
                <label className='cursor-pointer pt-1 text-xs font-medium text-blue-600 hover:text-blue-800'>
                  Changer l&apos;image
                  <input type='file' accept='image/*' onChange={onCoverChange} className='hidden' />
                </label>
              )}
            </div>
          </Field>

          <Field label='Tags (separes par des virgules)'>
            <input
              type='text'
              value={form.tagsInput}
              onChange={e => onFieldChange('tagsInput', e.target.value)}
              placeholder='petanque, technique, debutant'
              className={inputClass()}
            />
          </Field>

          <div className='flex gap-3 pt-4'>
            <button
              type='submit'
              disabled={isPending}
              className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50'
            >
              <Save className='h-4 w-4' />
              {isPending ? pendingLabel : submitLabel}
            </button>
            <button
              type='button'
              onClick={() => navigate(listRoute)}
              className='rounded-lg border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
            >
              Annuler
            </button>
          </div>
        </form>

        {showCategoryManager && (
          <CategoryManager open={catManagerOpen} onClose={() => setCatManagerOpen(false)} scope={categoryScope} />
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className='mb-2 block text-sm font-semibold text-slate-900'>{label}</label>
      {children}
      {error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
    </div>
  );
}

function inputClass(error?: string) {
  return `w-full rounded-lg border px-4 py-3 text-sm focus:outline-none transition ${
    error ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
  }`;
}
